import { Controller } from "@hotwired/stimulus";

import { IS_CYPRESS } from "@bryq/employers-src/settings";
import { getCustomPropertyValue } from "@bryq/employers-src/utils/css";
import Highcharts from "@bryq/employers-src/utils/highcharts";
import { readJsonScriptValue } from "@bryq/employers-src/utils/json_script";

/**
 * Adds a `floor` helper to Highcharts Templating.
 */
Highcharts.Templating.helpers.floor = (value) => Math.floor(value);

/**
 * Represent sten score related to different indicator groups for a candidate
 * and a pool of candidates on a spider chart.
 * Animations are deactivated for Cypress snapshots
 */
export default class extends Controller {
  static targets = ["chartContainer"];

  static classes = ["xAxisLabel"];

  static values = {
    id: String,
  };

  connect() {
    const data = readJsonScriptValue(this.idValue);
    this.renderChart(data);
  }

  renderChart(data) {
    const colorAccentBlue70 = getCustomPropertyValue("--color-accent-blue-70");
    const colorNeutral20 = getCustomPropertyValue("--color-neutral-20");
    const colorNeutral40 = getCustomPropertyValue("--color-neutral-40");
    const colorNeutral60 = getCustomPropertyValue("--color-neutral-60");
    const colorNeutral80 = getCustomPropertyValue("--color-neutral-80");
    const { xAxisLabelClass } = this;

    // We need a line color for when there is a 0 and the next area looks like
    // a line.
    // These colors are used as a stroke color that looks like the fill color,
    // but without the opacity, otherwise it would blend with the fill color
    // and look like a border.
    const scoreLineColor = this.blendOverWhite(
      Highcharts.color(colorAccentBlue70).setOpacity(0.5),
    );
    const averageLineColor = this.blendOverWhite(
      Highcharts.color(colorNeutral20).setOpacity(0.5),
    );

    const series = [
      {
        pointPlacement: "on",
        type: "area",
        zIndex: 1,
        color: colorAccentBlue70,
        lineColor: scoreLineColor,
        lineWidth: 1.5,
        fillOpacity: 0.5,
        marker: { enabled: false, symbol: "circle" },
        dataLabels: {
          enabled: true,
          format: "{floor y}",
          allowOverlap: true,
          style: {
            color: colorNeutral80,
            textOutline: "none",
            fontWeight: 400,
          },
        },
        ...data.series[0],
      },
    ];
    if (data.series.length === 2) {
      series.push({
        pointPlacement: "on",
        type: "area",
        zIndex: 0,
        // Affects the color of the text in the tooltip.
        color: colorNeutral60,
        lineColor: averageLineColor,
        lineWidth: 1.5,
        // Forces the fill color back to colorNeutral20 with 0.5 opacity as
        // `fillOpacity` has no effect when `fillColor` is set.
        fillColor: Highcharts.color(colorNeutral20).setOpacity(0.5).get("rgba"),
        marker: { enabled: false, symbol: "circle" },
        ...data.series[1],
      });
    }

    const chartOptions = {
      chart: {
        polar: true,
        type: "line",
        animation: IS_CYPRESS === false,
      },
      plotOptions: {
        series: {
          animation: IS_CYPRESS === false,
        },
      },
      title: {
        text: null,
      },
      colors: [colorAccentBlue70, colorNeutral20],
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: data.x_axis_scale_domain,
        gridLineColor: colorNeutral40,
        gridLineWidth: 0.5,
        tickmarkPlacement: "on",
        lineWidth: 0,
        labels: {
          useHTML: true,
          formatter() {
            return `<span class="body-medium-regular color-neutral-90 ${xAxisLabelClass}">${this.value}</span>`;
          },
        },
      },
      yAxis: {
        gridLineColor: colorNeutral40,
        gridLineInterpolation: "circle",
        gridLineWidth: 0.5,
        lineWidth: 0,
        min: data.y_axis_scale_domain[0],
        max: data.y_axis_scale_domain[1],
        tickAmount: 6,
        labels: { enabled: false },
      },
      tooltip: {
        outside: true,
        shared: true,
        pointFormat:
          '<span style="color:{series.color};">{series.name}: <b>{floor point.y}</b><br/>',
      },
      series,
    };
    Highcharts.chart(this.chartContainerTarget, chartOptions);
  }

  /**
   * Blends an RGBA color over white, so it loses its alpha channel, but still
   * is the same color it would have been over a white background.
   */
  // eslint-disable-next-line class-methods-use-this
  blendOverWhite(color) {
    const { rgba } = color;
    const alpha = rgba[3] !== undefined ? rgba[3] : 1;

    const blendedRGB = rgba
      .slice(0, 3)
      .map((c) => Math.round(alpha * c + (1 - alpha) * 255));

    return `rgb(${blendedRGB.join(",")})`;
  }
}
